import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Sun, Moon } from 'lucide-react';

const AuthPage = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back');
    } catch (error) {
      const message = error.message || 'Authentication failed';
      toast.error(message);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      toast.error('Enter your email address first');
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setResetSent(true);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
    }
    setResetLoading(false);
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#09090b]' : 'bg-[#f9f9f9]'}`}>
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        {/* Base brand gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? '#0a0a0f'
              : '#f4f2f7',
          }}
        />
        {/* Colorful blurred blobs */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(60% 50% at 20% 15%, rgba(88, 111, 141, ${isDark ? '0.55' : '0.45'}) 0%, transparent 60%),
              radial-gradient(55% 45% at 85% 30%, rgba(167, 159, 183, ${isDark ? '0.45' : '0.55'}) 0%, transparent 65%),
              radial-gradient(70% 60% at 30% 90%, rgba(118, 98, 172, ${isDark ? '0.65' : '0.55'}) 0%, transparent 65%),
              radial-gradient(50% 45% at 95% 95%, rgba(204, 93, 93, ${isDark ? '0.35' : '0.25'}) 0%, transparent 60%)
            `,
            filter: 'blur(40px)',
          }}
        />
        {/* Grain overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.6 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
            backgroundSize: '200px 200px',
            opacity: isDark ? 0.35 : 0.25,
            mixBlendMode: 'overlay',
          }}
        />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div>
            <img
              src={isDark ? '/tutortracer_logowithtext_white_trpt.png' : '/tutortracer_logowithtext_black_trpt.png'}
              alt="TutorTracer"
              className="h-10 w-auto"
            />
            <p className={`mt-2 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#888888]'}`}>Tutor Intelligence</p>
          </div>
          
          <div className="max-w-md">
            <h2 className={`text-4xl font-light leading-tight font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              See every lesson.<br />
              <span className="accent-gradient-text font-medium">Know every risk.</span>
            </h2>
            <p className={`mt-6 leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>
              AI-powered quality assurance for tutoring organizations.
              Monitor sessions, flag issues, and celebrate great teaching.
            </p>
          </div>
          
          <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#888888]'}`}>
            Trusted by tutoring organizations worldwide
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={`absolute top-6 right-6 p-2 rounded-lg transition-colors ${
            isDark 
              ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' 
              : 'text-[#666666] hover:text-[#1a1a1a] hover:bg-[#e8e8e8]'
          }`}
          data-testid="auth-theme-toggle"
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-12 text-center">
            <img
              src={isDark ? '/tutortracer_logowithtext_white_trpt.png' : '/tutortracer_logowithtext_black_trpt.png'}
              alt="TutorTracer"
              className="h-8 w-auto mx-auto"
            />
            <p className={`text-sm mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#888888]'}`}>Tutor Intelligence</p>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className={`text-xl font-medium font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                Welcome back
              </h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>
                Sign in to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className={isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}>Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`h-11 ${isDark ? 'bg-[#121212] border-[#27272a] focus:border-[#3f3f46]' : 'bg-white border-[#e5e5e5] focus:border-[#d0d0d0]'}`}
                  required
                  data-testid="auth-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className={isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}>Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`h-11 ${isDark ? 'bg-[#121212] border-[#27272a] focus:border-[#3f3f46]' : 'bg-white border-[#e5e5e5] focus:border-[#d0d0d0]'}`}
                  required
                  data-testid="auth-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full h-11 font-medium btn-press ${isDark ? 'accent-gradient text-white' : 'bg-[#1a1a1a] hover:bg-[#333333] text-white'}`}
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>

              <p className={`text-xs text-center leading-relaxed ${isDark ? 'text-[#71717a]' : 'text-[#888888]'}`}>
                By signing in, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className={`underline underline-offset-2 ${isDark ? 'hover:text-white' : 'hover:text-[#1a1a1a]'}`}>Terms of Service</a>
                {' '}and{' '}
                <a href="https://www.tutortracer.com/privacy-policy" target="_blank" rel="noopener noreferrer" className={`underline underline-offset-2 ${isDark ? 'hover:text-white' : 'hover:text-[#1a1a1a]'}`}>Privacy Policy</a>.
              </p>
            </form>

            <div className="text-center">
              {resetSent ? (
                <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>
                  Check your email for a reset link
                </p>
              ) : (
                <button
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  className={`text-sm transition-colors ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'}`}
                >
                  {resetLoading ? 'Sending...' : 'Forgot password?'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
